/**
 * ADR-0224 — Releases: the served shapes the admin page reads (mirrors
 * atlas-hub src/releases/types.ts §4.2; the hub composes every sentence —
 * date, time, result, recorded-by — so nothing here words a fact) and the
 * pure helpers the page's tests hold: the tab table, the composer's
 * reference parser, the group head's pull-request line.
 */

export const RELEASE_REPOS = ["hub", "console", "connector", "droplet"] as const;
export type ReleaseRepo = (typeof RELEASE_REPOS)[number];

export type ChangeState = "planned" | "in_flight" | "shipped";
export type ReleaseKind = "deploy" | "hotfix" | "maintenance";

export interface ChangeRef {
  repo: ReleaseRepo;
  number?: number;
  commit?: string;
  url: string | null;
}

export interface Change {
  id: string;
  feature: string;
  meaning: string;
  repos: ReleaseRepo[];
  refs: ChangeRef[];
  state: ChangeState;
  planned_window: string | null;
  entered_dev_at: string | null;
  shipped_at: string | null;
  deploy_id: string | null;
  meaning_by: { name: string } | null;
  unnamed: boolean;
  created_at: string;
  updated_at: string;
}

export interface PullRequestRef {
  repo: "hub" | "console";
  number: number;
  url: string;
  title?: string;
}

export interface DeployGroup {
  id: string;
  kind: ReleaseKind;
  state: "planned" | "shipped";
  at: string;
  ended_at: string | null;
  timezone: string;
  date_words: string;
  time_words: string;
  result_words: string | null;
  result: { ok: boolean; health_status: number | null; note: string | null } | null;
  commit: string | null;
  console_commit: string | null;
  pull_requests: PullRequestRef[];
  announced_where: string | null;
  recorded_by: { machine: boolean; name: string | null; meaning_by: { name: string } | null };
  recorded_words: string;
  changes: Change[];
}

export interface Counts {
  planned: number;
  in_flight: number;
  shipped: number;
}

export interface WeekView {
  start: string;
  end: string;
  timezone: string;
  words: string;
  counts: { deploys: number; shipped: number; in_flight: number; scheduled: number };
  shipped: Array<{ feature: string; meaning: string; shipped_at: string }>;
  scheduled: Array<{ id: string; at: string; date_words: string; time_words: string; changes: Array<{ feature: string }> }>;
  renderings: {
    collab_panel: { title: string; lines: string[]; more: number };
    deck_slide: { kicker: string; heading: string; lines: string[] };
    assistant_line: string;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Tabs — route segments (UI_GUIDELINES §2.11; spec §3.1)
// ─────────────────────────────────────────────────────────────────────────────

export type ReleasesTab = "shipped" | "planned" | "in-flight" | "this-week";

export const RELEASES_TABS: ReadonlyArray<{ key: ReleasesTab; label: string; href: string; count: keyof Counts | null }> = [
  { key: "planned", label: "Planned", href: "/admin/releases/planned", count: "planned" },
  { key: "in-flight", label: "In flight", href: "/admin/releases/in-flight", count: "in_flight" },
  { key: "shipped", label: "Shipped", href: "/admin/releases", count: "shipped" },
  { key: "this-week", label: "This week", href: "/admin/releases/this-week", count: null },
];

export const KIND_WORDS: Readonly<Record<ReleaseKind, string>> = { deploy: "Shipped", hotfix: "Hotfix", maintenance: "Maintenance" };

/** "#285" or the short commit — how a reference reads on a row (the hub's refWords, mirrored for the chip). */
export function refLabel(ref: ChangeRef): string {
  if (ref.number != null) return `#${ref.number}`;
  return ref.commit ? ref.commit.slice(0, 7) : "";
}

// ─────────────────────────────────────────────────────────────────────────────
// The composer's reference input (spec §3.3, errata E10 iii)
// ─────────────────────────────────────────────────────────────────────────────

export type RefInput = { repo: ReleaseRepo; number?: number; commit?: string };

const SHA = /^[0-9a-f]{7,40}$/i;

/**
 * "#285, console #215, 629996e" → refs. A bare number or commit goes to the
 * one repo chosen when exactly one is; with several repos chosen each
 * reference names its repo ("console #215", "hub@629996e"). Returns the
 * refusal to show under the field instead of a list.
 */
export function parseRefInput(text: string, repos: readonly ReleaseRepo[]): { refs: RefInput[] } | { error: string } {
  const tokens = text
    .split(/[,\n;]+|\s{2,}/)
    .map((t) => t.trim())
    .filter(Boolean);
  const refs: RefInput[] = [];
  const single = repos.length === 1 ? repos[0]! : null;
  for (const token of tokens) {
    const m = /^(?:(hub|console|connector|droplet)\s*[:#@ ]?\s*)?#?([^\s#@:]+)$/i.exec(token);
    if (!m) return { error: `"${token}" is not a reference — a pull request number (#285) or a commit (629996e), with its repo when several are chosen.` };
    const repo = (m[1]?.toLowerCase() as ReleaseRepo | undefined) ?? single;
    const value = m[2]!;
    if (!repo) return { error: `"${token}" needs its repo when several are chosen — "console #215" or "hub 629996e".` };
    if (!repos.includes(repo)) return { error: `"${token}" names ${repo}, which is not one of this change's repos.` };
    if (/^\d+$/.test(value)) {
      if (repo !== "hub" && repo !== "console") return { error: `${repo} has no pull requests — give a commit for it.` };
      refs.push({ repo, number: Number(value) });
    } else if (SHA.test(value)) {
      refs.push({ repo, commit: value.toLowerCase() });
    } else {
      return { error: `"${token}" is neither a pull request number nor a 7–40 hex commit.` };
    }
  }
  return { refs };
}

/** The group head's pull-request runs: [{repo, prs}] in repo order, hub first. */
export function pullRequestRuns(prs: readonly PullRequestRef[]): Array<{ repo: "hub" | "console"; prs: PullRequestRef[] }> {
  const out: Array<{ repo: "hub" | "console"; prs: PullRequestRef[] }> = [];
  for (const repo of ["hub", "console"] as const) {
    const mine = prs.filter((p) => p.repo === repo).sort((a, b) => a.number - b.number);
    if (mine.length) out.push({ repo, prs: mine });
  }
  return out;
}

/** A change's refs as the row shows them: the ref and where it opens. */
export function refLinks(refs: readonly ChangeRef[]): Array<{ label: string; url: string | null; repo: ReleaseRepo }> {
  return refs.map((r) => ({ label: refLabel(r), url: r.url, repo: r.repo }));
}

/** The composer's refs → the hub's body shape. */
export function refsBody(refs: readonly RefInput[]): Array<{ repo: ReleaseRepo; number?: number; commit?: string }> {
  return refs.map((r) => (r.number != null ? { repo: r.repo, number: r.number } : { repo: r.repo, commit: r.commit }));
}

/** The Plan-a-deploy quick create: a date and a time in the browser's zone → an instant. */
export function plannedAt(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null;
  const d = new Date(`${date}T${time}:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// ─────────────────────────────────────────────────────────────────────────────
// The tenant side (spec §3.6, §4.2) — what every workspace reads; no commit,
// no result, no recorder, no author.
// ─────────────────────────────────────────────────────────────────────────────

export interface TenantChange {
  feature: string;
  meaning: string;
  repos: ReleaseRepo[];
  refs: Array<{ repo: ReleaseRepo; label: string; url: string | null }>;
}

export interface TenantDeployGroup {
  id: string;
  kind: ReleaseKind;
  at: string;
  date_words: string;
  time_words: string;
  pull_requests: Array<{ repo: "hub" | "console"; number: number; url: string }>;
  announced_where: string | null;
  changes: TenantChange[];
}

export interface TenantWeek {
  start: string;
  end: string;
  words: string;
  shipped: Array<{ feature: string; meaning: string; shipped_at: string }>;
  in_flight_count: number;
  scheduled: Array<{ at: string; date_words: string; time_words: string; features: string[] }>;
  panel: { title: string; lines: string[]; more: number };
  assistant_line: string;
}

/** The What's new header: "Atlas changed · week of November 2, 2026" (§3.6). */
export function whatsNewHeader(week: Pick<TenantWeek, "words">): string {
  return `Atlas changed · ${week.words.replace(/^Week/, "week")}`;
}
